'use client';

import { useState, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import './toast-animations.css';

export interface ToastProps {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
  duration?: number;
  onClose?: (id: string) => void;
}

export function Toast({
  id,
  title,
  description,
  variant = 'default',
  duration = 5000,
  onClose,
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (duration > 0) {
      // eslint-disable-next-line no-undef
      const timer = setTimeout(() => {
        setIsVisible(false);
        // eslint-disable-next-line no-undef
        setTimeout(() => onClose?.(id), 300);
      }, duration);
      // eslint-disable-next-line no-undef
      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);

  const handleClose = () => {
    setIsVisible(false);
    // eslint-disable-next-line no-undef
    setTimeout(() => onClose?.(id), 300);
  };

  const variantStyles = {
    default: 'bg-white border-gray-200 text-gray-900 toast-info',
    destructive: 'bg-red-50 border-red-200 text-red-900 toast-error',
    success: 'bg-green-50 border-green-200 text-green-900 toast-success',
  };

  if (!isVisible) return null;

  return (
    <div
      className={`toast-hover fixed top-4 right-4 z-50 w-80 max-w-sm rounded-lg border p-4 shadow-lg ${
        isVisible ? 'toast-enter' : 'toast-exit'
      } ${variantStyles[variant]}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          {title && <div className="text-sm font-semibold">{title}</div>}
          {description && <div className="mt-1 text-sm">{description}</div>}
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="toast-close-btn ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600">
          <IoMdClose className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function ToastContainer({
  toasts,
  onClose,
}: {
  toasts: ToastProps[];
  onClose: (id: string) => void;
}) {
  return (
    <div className="fixed top-4 right-4 z-50 flex w-80 max-w-sm flex-col space-y-3">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="toast-bounce w-full"
          style={{
            animationDelay: `${index * 150}ms`,
          }}>
          <Toast {...toast} onClose={onClose} />
        </div>
      ))}
    </div>
  );
}

// Toast hook
export function useToast() {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const addToast = (toast: Omit<ToastProps, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const toast = {
    success: (title: string, description?: string) =>
      addToast({ title, description, variant: 'success' }),
    error: (title: string, description?: string) =>
      addToast({ title, description, variant: 'destructive' }),
    info: (title: string, description?: string) =>
      addToast({ title, description, variant: 'default' }),
    warning: (title: string, description?: string) =>
      addToast({ title, description, variant: 'default' }),
  };

  return { toast, toasts, removeToast };
}
